import Layout from "../components/site/Layout";
import { PageHeader, SectionLabel, SectionHeading } from "../components/site/Primitives";
import NextStep from "../components/site/NextStep";
import { ArrowUpRight } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { FIRM } from "../lib/firm";
import { trackEvent } from "../lib/analytics";

const TIERS = [
  {
    tier: "TIER I",
    title: "Retirement Tax Risk Analysis",
    stage: "Stage 1 / 03",
    body:
      "A diagnostic that quantifies your projected tax exposure across six core risk areas and identifies the size of the opportunity. Most clients discover six- or seven-figure tax liabilities they did not know they had.",
  },
  {
    tier: "TIER II",
    title: "Retirement Tax Strategy Plan",
    stage: "Stage 2 / 03",
    body:
      "A comprehensive written plan detailing the recommended strategies, sequencing, projected savings, and implementation roadmap across all six risk areas — specific to your situation.",
  },
  {
    tier: "TIER III",
    title: "Implementation & Oversight",
    stage: "Stage 3 / 03",
    body:
      "Ongoing coordination across your CPA, financial advisor, tax attorney, and other professionals. We manage the process so you don't have to.",
  },
];

const SERVICES = [
  {
    title: "Retirement Tax Risk Analysis",
    body: "Six-area diagnostic identifying projected lifetime tax exposure.",
  },
  {
    title: "Roth Conversion Planning",
    body: "Timing, sequencing, and bracket management across the pre-RMD conversion window.",
  },
  {
    title: "Advanced Conversion Structures",
    body: "IRA-LLC strategies for clients with $1M+ in traditional IRA balances.",
    link: { label: "Read methodology", to: "/advanced-conversion-strategies" },
  },
  {
    title: "RMD Exposure Reduction",
    body: "Strategies to reduce or delay required minimum distributions.",
  },
  {
    title: "IRMAA Medicare Surcharge Mitigation",
    body: "Income management to avoid Medicare surcharge tiers.",
  },
  {
    title: "Legislative Risk & Tax Diversification",
    body:
      "Post-OBBBA planning that balances current law against the possibility of future rate changes.",
  },
  {
    title: "Widow's Tax Penalty Planning",
    body: "Income equalization and survivor benefit strategies for married couples.",
  },
  {
    title: "IRA Inheritance Planning",
    body: "Tax-efficient transfer strategies under the SECURE Act 10-year rule.",
  },
];

export default function ServicesPage() {
  return (
    <Layout pageName="services">
      <PageHeader
        eyebrow="Services & Engagement"
        title="How we work: a structured engagement,"
        italicTail="not a one-time meeting."
        intro="Every client engagement follows a deliberate three-stage process. We begin with a diagnostic, not a pitch. You will know the size of the opportunity before you make any commitment to proceed."
      />

      <section className="py-20 lg:py-28 bg-cream-alt" data-testid="tiers-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-3 gap-px bg-sage/30 border border-sage/30">
            {TIERS.map((t, i) => (
              <div
                key={t.tier}
                data-testid={`tier-${i + 1}`}
                className="bg-cream p-8 lg:p-10 flex flex-col"
              >
                <div className="text-[10px] tracking-overline uppercase font-semibold text-accent-gold">
                  {t.tier}
                </div>
                <h3 className="mt-6 font-serif text-deep-teal text-3xl leading-snug">
                  {t.title}
                </h3>
                <p className="mt-5 text-charcoal-teal text-sm leading-relaxed">
                  {t.body}
                </p>
                <div className="mt-auto pt-10 text-[10px] tracking-overline uppercase font-semibold text-deep-teal/60">
                  {i + 1} · {t.stage}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-32 bg-cream" data-testid="catalog-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <SectionLabel index="Catalog" label="Service Areas" />
          <SectionHeading italicTail="one coordinated plan.">
            Eight focus areas,
          </SectionHeading>
          <p className="mt-6 text-charcoal-teal max-w-2xl">
            Each engagement draws from the relevant service areas based on
            your diagnostic findings.
          </p>

          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-sage/30 border border-sage/30">
            {SERVICES.map((s, i) => (
              <div
                key={s.title}
                data-testid={`service-${i + 1}`}
                className="bg-cream p-7 lg:p-8 flex flex-col"
              >
                <div className="text-[10px] tracking-overline uppercase font-semibold text-accent-gold mb-4">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <h3 className="font-serif text-deep-teal text-xl leading-snug">
                  {s.title}
                </h3>
                <p className="mt-3 text-charcoal-teal text-sm leading-relaxed flex-1">
                  {s.body}
                </p>
                {s.link && (
                  <Link
                    to={s.link.to}
                    className="mt-5 inline-flex items-center gap-2 text-xs tracking-overline uppercase font-semibold text-accent-gold editorial-link"
                  >
                    {s.link.label}
                    <ArrowUpRight size={14} weight="light" />
                  </Link>
                )}
              </div>
            ))}
          </div>

          <div className="mt-14 flex flex-wrap items-center gap-6">
            <a
              href={FIRM.calendly15}
              target="_blank"
              rel="noreferrer"
              onClick={() =>
                trackEvent("calendly_click_15min", {
                  meta: { source: "services_cta" },
                })
              }
              data-testid="services-primary-cta"
              className="group inline-flex items-center gap-3 px-7 py-4 bg-accent-gold !text-white text-xs tracking-overline uppercase font-medium hover:bg-accent-gold-hover transition-all"
            >
              Schedule a Retirement Tax Risk Analysis
              <ArrowUpRight
                size={16}
                weight="light"
                className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform"
              />
            </a>
            <Link
              to="/for-advisors"
              className="inline-flex items-center gap-2 text-sm text-deep-teal editorial-link"
            >
              For CPAs and Financial Advisors
              <ArrowUpRight size={16} weight="light" />
            </Link>
          </div>
        </div>
      </section>

      <NextStep />
    </Layout>
  );
}
